import getCategoryOrder from "components/financials/getCategoryOrder";
import { useIndicators } from "components/financials/modern/data/hooks";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import PagedIndicatorsGroup from "components/financials/modern/indicators/PagedIndicatorsGroup";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import { getVerticalsFromPlatformVertical } from "components/financials/modern/utils/platformVertical";
import { Dialog, DialogContent, DialogTitle, InlineDialogClose } from "components/ui/Dialog";
import { Popover, PopoverAnchor, PopoverContent } from "components/ui/Popover";
import classNames from "components/ui/classNames";
import { useIsPossiblyMobileDevice } from "components/ui/hooks/deviceDetection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs/v2/TabsV2";
import useTheme from "components/ui/useTheme";
import { noop, orderBy } from "lodash";
import { GitBranchPlusIcon } from "lucide-react";
import { matchSorter } from "match-sorter";
import { startTransition, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { FormattedMessage, useIntl } from "react-intl";
import type { IIndicatorDataSource, IPlatformVertical } from "types/graphqlTypes";

interface SelectArbitraryIndicatorPopoverProps {
  vertical: IPlatformVertical;
  indicatorDataSource: IIndicatorDataSource;
  mode?: "TOGGLE";
  limit?: number;
  selectedIndicators?: IIndicatorFieldsFragment[];
  onToggleIndicator?: (
    indicator: IIndicatorFieldsFragment,
    isSelected: boolean,
    bestPlacement?: "START" | "END",
  ) => void;
  onSelectIndicator?: (indicator: IIndicatorFieldsFragment, bestPlacement?: "START" | "END") => void;
}
export default function SelectArbitraryIndicatorPopover({
  vertical,
  indicatorDataSource,
  mode,
  limit,
  selectedIndicators = [],
  onSelectIndicator = noop,
  onToggleIndicator = noop,
}: SelectArbitraryIndicatorPopoverProps) {
  const intl = useIntl();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isOpen = isPopoverOpen || isDialogOpen;
  const { loading, indicators } = useIndicators({
    filterByDataSource: [indicatorDataSource],
    filterByIndicatorVertical: getVerticalsFromPlatformVertical(vertical, indicatorDataSource),
  });
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();

  const searchQueryRef = useRef<HTMLInputElement | null>(null);
  useHotkeys(
    "ctrl+f, meta+f, f3",
    (event) => {
      if (isOpen) {
        event.preventDefault();
        searchQueryRef.current?.focus();
      }
    },
    {
      preventDefault: isOpen,
      ignoreModifiers: false,
    },
  );

  const filteredIndicators = matchSorter(indicators, searchQuery, {
    keys: ["documentation.*.name", "documentation.*.abbreviation"],
    keepDiacritics: false,
    threshold: matchSorter.rankings.CONTAINS,
    sorter: (rankedItems) => {
      if (searchQuery) {
        return [...rankedItems].sort((a, b) => {
          return b.rank - a.rank;
        });
      }
      return orderBy(
        rankedItems.map((rankedItem) => ({
          item: rankedItem,
          categoryOrder: getCategoryOrder(rankedItem.item.category),
          name: getIndicatorDocumentation(intl, rankedItem.item).name,
        })),
        ["categoryOrder", "name"],
        ["asc", "asc"],
      ).map(({ item }) => item);
    },
  });

  const onlyUnselectedIndicators = filteredIndicators.filter((indicator) => {
    return !selectedIndicators.some(({ objectId }) => objectId === indicator.objectId);
  });

  const onlySelectedIndicators = indicators.filter((indicator) => {
    return selectedIndicators.some(({ objectId }) => objectId === indicator.objectId);
  });

  const [tab, setTab] = useState<string>(() => {
    return onlySelectedIndicators.length > 0 ? "SELECTED" : "AVAILABLE";
  });

  useEffect(() => {
    // We're trying to push the user to select an indicator
    if (searchQuery && tab === "SELECTED" && onlyUnselectedIndicators.length > 0) {
      setTab("AVAILABLE");
    }
  }, [searchQuery]);

  useEffect(() => {
    // Bring back the tab to where it was before
    if (!isOpen && tab === "AVAILABLE" && onlySelectedIndicators.length > 0) {
      setTab("SELECTED");
    }
    if (!isOpen && tab === "SELECTED" && onlySelectedIndicators.length === 0) {
      setTab("AVAILABLE");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && limit && onlySelectedIndicators.length < limit && tab === "SELECTED") {
      setTab("AVAILABLE");
    }
  }, [isOpen]);

  useEffect(() => {
    if (tab === "SELECTED" && onlySelectedIndicators.length === 0 && onlyUnselectedIndicators.length > 0) {
      setTab("AVAILABLE");
    }
    if (tab === "AVAILABLE" && limit && limit > 0 && onlySelectedIndicators.length === limit) {
      setTab("SELECTED");
    }
  }, [onlySelectedIndicators.length]);

  const isPossiblyMobileDevice = useIsPossiblyMobileDevice();
  const bestPlacementOnSelect = isPossiblyMobileDevice ? "START" : "END";

  const tinyTriggerButton = (
    <button
      disabled={loading || indicators.length === 0}
      type="button"
      onClick={() => {
        setIsPopoverOpen(!isPopoverOpen);
        setIsDialogOpen(false);
      }}
      className={classNames(
        "hidden sm:inline-flex",
        "rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600",
        "relative dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
        {
          "bg-violet-50 text-violet-600 dark:bg-violet-600/20 dark:text-violet-200":
            isOpen || onlySelectedIndicators.length > 0,
        },
        "focus-visible:outline-none",
        "focus-visible:ring-1 focus-visible:ring-offset-1",
        "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
        "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
        "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
      )}
    >
      <GitBranchPlusIcon strokeWidth={2} />
      {onlySelectedIndicators.length > 0 && (
        <div className="absolute left-[70%] top-[-30%] box-border appearance-none">
          <span
            className={classNames(
              "box-border inline-flex h-5 w-5 select-none appearance-none items-center justify-center",
              "bg-red-500 pb-[1px] pl-[1px] text-xs font-bold text-white squircle",
              "dark:bg-rose-900 dark:text-rose-100",
            )}
          >
            {onlySelectedIndicators.length}
          </span>
        </div>
      )}
    </button>
  );
  const fullWidthTriggerButton = (
    <button
      disabled={loading || indicators.length === 0}
      type="button"
      onClick={() => {
        setIsDialogOpen(true);
        setIsPopoverOpen(false);
      }}
      className={classNames(
        `
inline-flex
w-full items-center justify-between rounded-xl border border-slate-200
bg-slate-50/60 p-2 text-slate-600
dark:border-slate-950
dark:bg-slate-700
dark:text-slate-300
sm:hidden
        `,
      )}
    >
      <div className="flex items-center gap-x-2 text-sm font-medium">
        <GitBranchPlusIcon strokeWidth={2} className="size-4" />
        <FormattedMessage defaultMessage="Indicadores" id="GUCl4s" />
      </div>

      <span
        className={classNames(
          "box-border inline-flex items-center justify-center space-x-1.5 rounded-full border border-transparent px-2.5 py-0.5",
          "bg-red-500 text-xs font-bold text-white",
          "dark:bg-slate-800",
          { "dark:border-violet-300/20 dark:bg-violet-950/20": onlySelectedIndicators.length },
        )}
      >
        <span
          className={classNames("text-inherit", {
            "dark:text-violet-300": onlySelectedIndicators.length,
          })}
        >
          {onlySelectedIndicators.length}
        </span>
        <span>/</span>
        <span>{indicators.length}</span>
      </span>
    </button>
  );

  const selectContent = (
    <>
      <input
        type="text"
        maxLength={26}
        onChange={(event) => {
          startTransition(() => {
            setSearchQuery(event.target.value);
          });
        }}
        onKeyDown={(event) => {
          // Confirm selection via Enter key
          if (event.key === "Enter" && onlyUnselectedIndicators.length === 1 && searchQuery !== "") {
            const [indicator] = onlyUnselectedIndicators;
            setIsDialogOpen(false);
            setIsPopoverOpen(false);
            if (mode === "TOGGLE") {
              onToggleIndicator(indicator, false, bestPlacementOnSelect);
              return;
            }
            onSelectIndicator(indicator, bestPlacementOnSelect);
          }
        }}
        spellCheck={false}
        autoComplete="off"
        ref={searchQueryRef}
        placeholder={intl.formatMessage({
          id: "OuKTbi",
          defaultMessage: "Pesquise um indicador avulso...",
        })}
        className={classNames(
          "flex w-full rounded-xl border border-slate-300 px-3 py-3 text-gray-900 sm:rounded-lg sm:px-2",
          "text-base sm:text-sm",
          "font-medium tracking-wide text-slate-600",
          "placeholder:text-slate-500 focus:placeholder:text-slate-500 dark:placeholder:text-slate-400",
          "bg-slate-100 dark:border dark:border-slate-950 dark:bg-slate-800 dark:text-white sm:dark:bg-slate-900",

          "border-transparent focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2",
          "ring-transparent focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-slate-300",

          "dark:focus-visible:text-white dark:focus-visible:ring-2 dark:focus-visible:ring-blue-300/40",
          "focus-visible:dark:ring-offset-transparent dark:focus-visible:placeholder:text-slate-300",
          "dark:focus-visible:bg-blue-600/10 sm:dark:focus-visible:bg-blue-600/10",
        )}
      />

      <Tabs className="w-full rounded-xl font-normal" value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full flex-1">
          <TabsTrigger className="w-full text-xs" value="AVAILABLE">
            <FormattedMessage
              id="xFVH4Y"
              defaultMessage="{totalCount, plural, =0 {Todos} other {Todos (#)}}"
              values={{
                totalCount: onlyUnselectedIndicators.length,
              }}
            />
          </TabsTrigger>
          <TabsTrigger className="w-full text-xs" value="SELECTED">
            <FormattedMessage
              id="YWoEuE"
              defaultMessage="{totalCount, plural, =0 {Selecionados} =1 {Selecionado (#)} other {Selecionados (#)}}"
              values={{
                totalCount: onlySelectedIndicators.length,
              }}
            />
          </TabsTrigger>
        </TabsList>

        <TabsContent className="border-none p-0" value="AVAILABLE">
          <PagedIndicatorsGroup
            mode={mode}
            limit={limit}
            searchQuery={searchQuery}
            indicators={onlyUnselectedIndicators}
            selectedIndicators={onlySelectedIndicators}
            onToggleIndicator={(indicator, isSelected) => {
              setIsDialogOpen(false);
              onToggleIndicator(indicator, isSelected, bestPlacementOnSelect);
            }}
            onSelectIndicator={(indicator) => {
              setIsDialogOpen(false);
              onSelectIndicator(indicator, bestPlacementOnSelect);
            }}
          />
        </TabsContent>
        <TabsContent className="border-none p-0" value="SELECTED">
          <PagedIndicatorsGroup
            mode={mode}
            indicators={onlySelectedIndicators}
            searchQuery={searchQuery}
            selectedIndicators={onlySelectedIndicators}
            onToggleIndicator={onToggleIndicator}
            onSelectIndicator={onSelectIndicator}
          />
        </TabsContent>
      </Tabs>
    </>
  );

  return (
    <>
      <Popover
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSearchQuery("");
          }

          setIsPopoverOpen(!isPopoverOpen);
        }}
      >
        <PopoverAnchor asChild={true}>{tinyTriggerButton}</PopoverAnchor>

        <PopoverContent
          side="bottom"
          align="start"
          sticky="always"
          className={classNames(
            "w-sm space-y-2 overflow-hidden rounded-xl border border-slate-300 p-2",
            "shadow-md shadow-slate-900/10",
            {
              "border-slate-900/80 bg-slate-900 text-slate-300 shadow-neutral-950/40": theme === "dark",
              dark: theme === "dark",
            },
          )}
        >
          {selectContent}
        </PopoverContent>
      </Popover>

      <Dialog open={isDialogOpen} onOpenChange={(isOpen) => setIsDialogOpen(isOpen)}>
        {fullWidthTriggerButton}
        <DialogContent
          className="rounded-none border-slate-300 bg-white dark:bg-slate-950 sm:rounded-3xl"
          hideCloseButton={true}
        >
          <div className="flex items-center justify-between">
            <DialogTitle>
              {intl.formatMessage(
                {
                  defaultMessage: "Pesquise indicadores ({totalCount})",
                  id: "/z9NgY",
                },
                {
                  totalCount: indicators.length,
                },
              )}
            </DialogTitle>
            <InlineDialogClose />
          </div>
          <div
            className={classNames("w-full space-y-2 overflow-hidden", {
              "text-slate-300": theme === "dark",
              dark: theme === "dark",
            })}
          >
            {selectContent}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
